import React, { useState, useEffect, useRef } from 'react';

const colors = {
    primary: '#1a1a1a',
    secondary: '#4c956c',
    accent: '#d9f2b4',
    light: '#f8faf5',
    neutral: '#336F4D',
    neutralLight: '#505151',
    cream: '#fffaf0',
    gold: '#d4af37',
    textDark: '#333'
};

export default function EnvironmentPage() {
    const [animatedElements, setAnimatedElements] = useState({});
    const [isPlaying, setIsPlaying] = useState(false);
    const videoRef = useRef(null);



    const handlePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setAnimatedElements((prev) => ({ ...prev, environment: true }));
                    }
                });
            },
            { threshold: 0.1 }
        );
        const section = document.getElementById('environment');
        if (section) observer.observe(section);
        return () => {
            if (section) observer.unobserve(section);
        };
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            const handlePlay = () => setIsPlaying(true);
            const handlePause = () => setIsPlaying(false);

            video.addEventListener('play', handlePlay);
            video.addEventListener('pause', handlePause);

            return () => {
                video.removeEventListener('play', handlePlay);
                video.removeEventListener('pause', handlePause);
            };
        }
    }, []);

    return (
        <section
            id="environment"
            className="environment-section animate-on-scroll"
            style={{
                padding: '6rem 2rem',
                background: 'linear-gradient(180deg, rgba(255,250,240,0.7) 0%, rgba(248,250,245,0.9) 100%)',
                fontFamily: '"Noto Serif TC","Microsoft JhengHei", "PingFang TC", serif',
                color: colors.textDark,
                minHeight: '100vh'
            }}
        >
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto'
            }}>
                {/* Header Section */}
                <h2
                    style={{
                        fontSize: '2rem',
                        fontWeight: 600,
                        fontFamily: '"Noto Serif TC","Microsoft JhengHei", "PingFang TC", serif',
                        textAlign: 'center',
                        color: colors.primary,
                        marginBottom: '1rem',
                        animation: animatedElements['environment'] ? 'fadeInUp 1s ease forwards' : 'none',
                        opacity: animatedElements['environment'] ? 1 : 0,
                    }}
                >
                    環境介紹
                </h2>

                {/* Decorative Line */}
                <div style={{
                    width: 'min(500px, 80%)',
                    height: '2px',
                    background: 'linear-gradient(90deg, #5C8989, rgba(92, 137, 137, 0.3))',
                    margin: '0 auto 1rem',
                    borderRadius: '1px',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '50%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                        animation: 'shine 3s infinite'
                    }} />
                </div>

                {/* Description */}
                <p style={{
                    fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                    color: colors.neutralLight,
                    maxWidth: '700px',
                    margin: '0 auto 4rem',
                    lineHeight: '1.8',
                    fontWeight: 500,
                    textAlign: 'center',
                    padding: '0 1rem',
                    animation: animatedElements['environment'] ? 'fadeIn 1.2s ease 0.3s forwards' : 'none',
                    opacity: animatedElements['environment'] ? 1 : 0
                }}>
                    走進我們的空間，感受寧靜與舒適的完美交融。每一處細節都經過精心設計，只為給您最極致的放鬆體驗。
                </p>

                {/* Video Section */}
                <div style={{
                    marginBottom: '4rem',
                    animation: animatedElements['environment'] ? 'fadeInUp 1s ease 0.5s forwards' : 'none',
                    opacity: animatedElements['environment'] ? 1 : 0,
                }}>
                    <div style={{
                        position: 'relative',
                        maxWidth: '900px',
                        margin: '0 auto',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                        backgroundColor: colors.primary,
                    }}>
                        <video
                            ref={videoRef}
                            style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                                maxHeight: '600px',
                                objectFit: 'cover'
                            }}
                            controls
                            autoPlay
                            muted
                            loop
                            playsInline
                        >
                            <source src="/assets/spa.mp4" type="video/mp4" />
                            您的瀏覽器不支援影片播放。
                        </video>

                        {/* Custom Play Button Overlay (optional) */}
                        {!isPlaying && (
                            <div
                                onClick={handlePlayPause}
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '80px',
                                    height: '80px',
                                    backgroundColor: 'rgba(76, 149, 108, 0.9)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    pointerEvents: videoRef.current?.paused ? 'auto' : 'none',
                                    opacity: videoRef.current?.paused ? 1 : 0
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)';
                                    e.currentTarget.style.backgroundColor = 'rgba(76, 149, 108, 1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                                    e.currentTarget.style.backgroundColor = 'rgba(76, 149, 108, 0.9)';
                                }}
                            >
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            </div>
                        )}
                    </div>
                </div>

                {/* Detailed Description */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.51)',
                    borderRadius: '16px',
                    padding: 'clamp(2.5rem, 5vw, 4rem)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                    animation: animatedElements['environment'] ? 'fadeIn 1s ease 0.7s forwards' : 'none',
                    opacity: animatedElements['environment'] ? 1 : 0,
                }}>
                    <h3 style={{
                        fontSize: 'clamp(1.5rem, 3.5vw, 2rem)',
                        color: colors.primary,
                        fontWeight: 600,
                        marginBottom: '2rem',
                        textAlign: 'center',
                        paddingBottom: '1.5rem',
                        borderBottom: `2px solid ${colors.accent}`,
                        letterSpacing: '2px'
                    }}>
                        京采再生 SPA
                    </h3>

                    <div style={{
                        maxWidth: '800px',
                        margin: '0 auto',
                        lineHeight: '2',
                        fontSize: 'clamp(1rem, 2vw, 1.15rem)',
                        color: colors.textDark
                    }}>
                        <p style={{
                            marginBottom: '1.8rem',
                            textAlign: 'justify',
                            textIndent: '2em'
                        }}>
                            在這個節奏飛快、壓力滿載的時代，京采悄悄為你保留一隅靜心之所——不只是讓肌膚變好，更是讓身心重新對話與修復。
                        </p>

                        <div style={{
                            margin: '2.5rem 0',
                            padding: '2rem',
                            background: `linear-gradient(135deg, ${colors.light} 0%, ${colors.cream} 100%)`,
                            borderRadius: '12px',
                            borderLeft: `4px solid ${colors.secondary}`
                        }}>
                            <p style={{
                                marginBottom: '1rem',
                                fontSize: 'clamp(1rem, 2.2vw, 1.2rem)',
                                color: colors.secondary,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <span style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: colors.secondary,
                                    borderRadius: '50%',
                                    display: 'inline-block'
                                }}></span>
                                「京采」精品般的精緻選擇
                            </p>
                            <p style={{
                                marginBottom: '1.5rem',
                                fontSize: 'clamp(1rem, 2.2vw, 1.2rem)',
                                color: colors.secondary,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <span style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: colors.secondary,
                                    borderRadius: '50%',
                                    display: 'inline-block'
                                }}></span>
                                「再生」身與心的溫柔重啟
                            </p>
                        </div>

                        <p style={{
                            marginBottom: '1.8rem',
                            textAlign: 'justify',
                            textIndent: '2em'
                        }}>
                            品牌創立於一個簡單卻真實的起點：我們不再滿足於傳統 SPA 只能「放鬆」、醫美卻過於「侵入」的兩難選擇。因此，京采決心打造一條嶄新的保養之路——結合生技成分 × 專業儀器 × 手技能量療癒，讓每一次保養都深層、安心又有感。
                        </p>

                        <p style={{
                            marginBottom: '1.8rem',
                            textAlign: 'justify',
                            textIndent: '2em'
                        }}>
                            我們嚴選通過多重檢測的韓國原裝胞泌體、PDRN 等再生等級活性成分，搭配專業儀器導入技術與經絡手技，從肌底提升自我修復力。真正實現「無侵入、看得見」的深層改變
                        </p>
                    </div>
                </div>
            </div>

            <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes shine {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }

        @media (max-width: 768px) {
          #environment {
            padding: 4rem 1rem !important;
          }
        }

        @media (max-width: 480px) {
          #environment {
            padding: 3rem 1rem !important;
          }
        }

        video::-webkit-media-controls-panel {
          background-image: linear-gradient(transparent, rgba(0,0,0,0.3)) !important;
        }
      `}</style>
        </section>
    );
}